import { InfoButton } from '~/components/primitives/InfoButton'
import type { WeekChecklist as Checklist, WeekChecklistItem } from '~/models/weekChecklist'
import styles from './WeekChecklist.module.css'

/**
 * What the week amounts to so far (D-099 §C).
 *
 * **Descriptive and supportive, and the prohibitions are explicit rather than
 * implied.** No streak, no grade, no percentage, no red failure state, no
 * "failed week", and no "3 of 5 done" — the model hands over `soFar` and
 * `target` as separate numbers precisely so this component is never given a
 * ratio to render.
 *
 * **State is never carried by colour alone.** Each item says where it is in
 * words; the mark beside it is a second channel, not the only one.
 *
 * **Completing everything does not mean the plan worked.** This describes what
 * was collected, never whether it was any good — that is the rules engine's job
 * and a different sentence on a different part of the screen.
 */

const STATE_LABELS: Readonly<Record<WeekChecklistItem['state'], string>> = {
  collected: 'Done',
  partiallyCollected: 'Started',
  notYet: 'Not yet',
}

/** A second channel beside the words, never the only one. */
const STATE_MARKS: Readonly<Record<WeekChecklistItem['state'], string>> = {
  collected: '✓',
  partiallyCollected: '–',
  notYet: '·',
}

export function WeekChecklist({ checklist }: { checklist: Checklist }) {
  if (checklist.items.length === 0) return null

  /* Outstanding items only. A consequence beside something already collected is
     a sentence about a problem the user does not have. */
  const outstanding = checklist.items.filter(
    (item) => item.state !== 'collected' && item.consequence !== undefined,
  )

  return (
    <section class={styles.region} aria-labelledby="week-checklist-heading">
      <div class={styles.header}>
        <h2 id="week-checklist-heading" class={styles.heading}>
          Your week so far
        </h2>

        {/*
          The costs are gathered into one sheet rather than repeated under each
          row (D-104 §5). **They are not deleted, and that distinction is the
          whole of this change** — C6.5 recorded that these sentences *are* the
          feature, because a checklist saying only what is missing, without
          saying what it costs, is the score this one exists to avoid. What
          changed is that four of them no longer sit permanently on the app's
          daily surface.

          The control is absent when nothing is outstanding, rather than opening
          an empty sheet.
        */}
        {outstanding.length === 0 ? null : (
          <InfoButton topic="what is still missing" title="What Project Sola cannot conclude yet">
            <ul class={styles.consequenceList}>
              {outstanding.map((item) => (
                <li key={item.id}>
                  <strong>{item.label}.</strong> {item.consequence}
                </li>
              ))}
            </ul>
          </InfoButton>
        )}
      </div>

      <ul class={styles.list}>
        {checklist.items.map((item) => (
          <li key={item.id} class={styles.item}>
            <p class={styles.line}>
              <span aria-hidden="true" class={styles.mark}>
                {STATE_MARKS[item.state]}
              </span>
              <span class={styles.label}>{item.label}</span>{' '}
              <span class={styles.count}>
                {/* Two numbers with a word between them, deliberately not "3/5".
                    A ratio invites a percentage, and a percentage is a score. */}
                {item.soFar} recorded, {item.target} planned — {STATE_LABELS[item.state]}
              </span>
            </p>
          </li>
        ))}
      </ul>

      {/* Shortened rather than moved. It names a capability the app deliberately
          does not have and points at the one place that changes it, which is the
          "name unbuilt capability rather than mocking it" rule — a pointer
          nobody can see is not a pointer. */}
      {checklist.hasPlannedItems ? null : (
        <p class={styles.note}>No weekly sessions planned yet. Add them in My Plan.</p>
      )}
    </section>
  )
}
